/** @jsx jsx */
import { jsx } from '@emotion/core'
import React, { useEffect, useRef, useState } from 'react'
import { observer } from 'mobx-react'
import _ from 'lodash'
import uuid from 'uuid'
import { SportsIco } from '@styles/match.style'
import { SPORT_ID_ICON_MAP } from '@shared/SportIdGroup'
import { BASIC_TEAM_EMBLEM_MAP } from '@shared/EmblemGroup'
import { TxtLimit } from '@styles/base.style'
import classNames from 'classnames'
import { isMobile } from 'react-device-detect'
import { Schema$CheerForTeamData } from '@protocol/chat'
import { SimpleCheerForTeamHistoryStyle } from '@views/Popup/Chat/index.style'
import { Button } from '@styles/button.style'
import {
  isClosedMatchStatus,
  isNotStartedMatchStatus,
  isOngoingMatchStatus,
  isPeriodScoreMatch,
  formatMatchStatusNameToCheerForTeamMatchStatusName,
} from '@utils/utils'
import useRootStore from '@store/useRootStore'
import { ChatMessageType } from '@interface/chat'
import * as ImageUtils from '@utils/image-utils'
import TimeIco from '../../containers/shared/Icon/MatchupIcon/TimeIco'

export interface SimpleCheerForTeamMatchInfoProps {
  chatMessageId?: number // chatMessage 의 id, not chatId
  selectedCheerForTeamData: Schema$CheerForTeamData
  className?: string
  darkBg?: boolean
}

export const SimpleCheerForTeamMatchInfo: React.FC<SimpleCheerForTeamMatchInfoProps> = observer((props) => {
  const { chattingStore } = useRootStore()
  const { chatMessageId, selectedCheerForTeamData, className = '', darkBg } = props
  const [isEmotionActive, setIsEmotionActive] = useState(false)
  const {
    matchId,
    sportId,
    matchStatus,
    matchStatusName,
    dateOfMatch,
    homeEmblem,
    homeName,
    homeScore: homeTeamScore,
    awayEmblem,
    awayName,
    awayScore: awayTeamScore,
    effectIconUrl,
    homePeriodScores,
    awayPeriodScores,
    createdAt = new Date(),
  } = selectedCheerForTeamData

  // 이모티콘이나 gif 이미지 정지 후 재생을 위해 터치시 suffix 의 값을 바꿔 동작 시키기 위해.
  // json string [{chatId, suffix}]
  const [addSuffixListText, setAddSuffixListText] = useState('[]')
  const emotionIconImageRef = useRef(null)

  if (!effectIconUrl) return

  const addSuffixList = JSON.parse(addSuffixListText)
  let addSuffix = _.find(addSuffixList, { matchId })

  const idMiddleSuffix = _.isNil(chatMessageId) ? `${matchId}` : `${matchId}_${chatMessageId}`
  const getTimeLastSuffix = (): string => {
    if (addSuffix) {
      return addSuffix.suffix
    }
    if (_.isNil(chatMessageId)) {
      return uuid.v4()
    }
    return ''
  }
  const timeLastSuffix = getTimeLastSuffix()
  const effectIconUrlSrc = `${effectIconUrl}?i=${idMiddleSuffix}&t=${timeLastSuffix}`

  const matchStatusClass = classNames('match-state',
    { 'live': isOngoingMatchStatus(matchStatus) },
    { 'not-started': isNotStartedMatchStatus(matchStatus) },
    { 'closed': isClosedMatchStatus(matchStatus) },
  )

  let homeScore = homeTeamScore
  let awayScore = awayTeamScore
  if (isOngoingMatchStatus(matchStatus) && isPeriodScoreMatch(sportId)) {
    if (!_.isEmpty(homePeriodScores) && !_.isEmpty(awayPeriodScores)) {
      homeScore = _.last(homePeriodScores)
      awayScore = _.last(awayPeriodScores)
    }
  }

  const homeScoreClassNames = classNames('home-team-score', {
    'win': homeScore > awayScore,
  })
  const awayScoreClassNames = classNames('away-team-score', {
    'win': homeScore < awayScore,
  })

  const homeSetScoreElements: React.ReactElement[] = []
  const awaySetScoreElements: React.ReactElement[] = []
  if (isPeriodScoreMatch(sportId)) {
    _.range(0, homeTeamScore).forEach(idx => {
      homeSetScoreElements.push(<span key={`simple_cheer_for_team_home_set_scores-${idx}`} className={`circle`}/>)
    })
    _.range(0, awayTeamScore).forEach(idx => {
      awaySetScoreElements.push(<span key={`simple_cheer_for_team_away_set_scores-${idx}`} className={`circle`}/>)
    })
  }

  const stopEffectIconImage = ImageUtils.findStopEffectIconImgCheerForTeam(effectIconUrl)
  const onClickCheerForTeamEmotion = () => {
    // 채팅리스트에서 응원글이모티콘을 클릭할때 정지이미지를 먼저 보여주고 onLoad()에서 애니메이션 이미지로 변경
    emotionIconImageRef.current.src = stopEffectIconImage
  }
  const updateTimeLastSuffixOfEffectIconUrlSrc = (): void => {
    if (addSuffix) {
      addSuffix.suffix = uuid.v4()
      _.remove(addSuffixList, { matchId })
    } else {
      addSuffix = { matchId, suffix: uuid.v4() }
    }
    addSuffixList.push(addSuffix)
    setAddSuffixListText(JSON.stringify(addSuffixList))
  }


  useEffect(() => {
    const { id: newMessageId, type: newMessageType } = chattingStore.newChatMessage ?? {}

    // 응원글 작성중 응원글 효과 선택할때(chatMessageId === undefined) 응원글 애니메이션이 커지지 않도록 처리
    if (_.isNil(newMessageId)) {
      return
    }

    // 채팅리스트에 새 메세지가 아닐때 응원글 애니메이션이 커지지 않도록 처리
    if (chatMessageId !== newMessageId) {
      return
    }

    // 새 메세지의 메세지 타입이 응원글이 아닐때 응원글 애니메이션이 커지지 않도록 처리
    if (newMessageType !== ChatMessageType.CHEER_FOR_TEAM) {
      return
    }

    // 채팅창이 닫혔을때 추가된 응원글이 채팅창을 열었을때 애니메이션이 커지지 않도록 처리.
    if (chatMessageId === chattingStore.chatMessageIdOnChatClosed) {
      return
    }

    setIsEmotionActive(true)
    const oneTimePlayImage = ImageUtils.findOneTimePlayEffectIconImgCheerForTeam(effectIconUrl)
    emotionIconImageRef.current.src = `${oneTimePlayImage}?i=${idMiddleSuffix}&t=${timeLastSuffix}`
  }, [])

  useEffect(() => {
    // 응원글 작성중 응원글이모티콘을 변경하면 정지이미지를 먼저 보여주고 onLoad()에서 애니메이션 이미지로 변경
    if (_.isNil(chatMessageId)) {
      emotionIconImageRef.current.src = stopEffectIconImage
    }
  }, [effectIconUrl])

  const cheerForTeamEmotionClass = classNames('cheer-for-team-emotion', {
    'active': isEmotionActive,
  })

  const onAnimationEnd = () => {
    setIsEmotionActive(false)
  }

  const previewBoxWrapName = classNames('preview-box-wrap', {
    'pc': !isMobile,
    [className!]: className,
    'dark': darkBg,
  })

  const matchStatusClasspName = classNames('match-status-name', {
    'live': isOngoingMatchStatus(matchStatus),
    'not-started': isNotStartedMatchStatus(matchStatus),
    'closed': isClosedMatchStatus(matchStatus),
  })

  const onLoad = (e): void => {
    // 응원글이모티콘에 정지이미지가 로드 되었을 때 애니메이션 이미지로 변경
    // [모바일웹] 응원글 효과 목록에서 효과 변경 시 기존에 선택되어 있던 효과가 재생되는 현상 0009539:0029286
    // https://task.zempublic.co.kr/view.php?id=9539#c29286
    if (e.target.src === stopEffectIconImage) {
      updateTimeLastSuffixOfEffectIconUrlSrc()
    }
  }

  const formattedMatchStatusName = formatMatchStatusNameToCheerForTeamMatchStatusName({
    matchStatus, matchStatusName, dateOfMatch, baseDateAt: createdAt,
  })

  return (
    <div className={previewBoxWrapName} css={SimpleCheerForTeamHistoryStyle}>
      <p className={matchStatusClasspName}>
        <TimeIco/>
        <span className="match-status-name-txt">
          {formattedMatchStatusName}
        </span>
      </p>
      <div className="preview-box">
        {!_.isEmpty(effectIconUrlSrc) &&
          <Button
            TP
            className={cheerForTeamEmotionClass}
            onAnimationEnd={onAnimationEnd}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              onClickCheerForTeamEmotion()
            }}
          >
            <img src={effectIconUrlSrc} alt="cheer-for-team-emotion" ref={emotionIconImageRef} onLoad={onLoad}/>
          </Button>
        }
        <div className="cheer-for-team-contents-wrap">
          <SportsIco className="sports-ico">
            {SPORT_ID_ICON_MAP[sportId] || SPORT_ID_ICON_MAP['178']}
          </SportsIco>
          <span className={matchStatusClass}/>

          <div className="match-info-wrap">
            <span className="team-info">
              <span className="team-emblem">
                <img src={homeEmblem || BASIC_TEAM_EMBLEM_MAP[sportId]} alt="홈팀 엠블럼" className="team-emblem-img" loading="lazy"/>
              </span>
              <span css={TxtLimit} className="home-team-name">{homeName}</span>
              <span className={homeScoreClassNames}>
                {isPeriodScoreMatch(sportId) && <div className="match-state">{homeSetScoreElements}</div>}
                {homeScore}
              </span>
            </span>
            <span className="team-info">
              <span className="team-emblem">
                <img src={awayEmblem || BASIC_TEAM_EMBLEM_MAP[sportId]} alt="홈팀 엠블럼" className="team-emblem-img" loading="lazy"/>
              </span>
              <span css={TxtLimit} className="away-team-name">{awayName}</span>
              <span className={awayScoreClassNames}>
              {isPeriodScoreMatch(sportId) && <div className="match-state">{awaySetScoreElements}</div>}
                {awayScore}
              </span>
            </span>
          </div>
        </div>
      </div>
    </div>
  )
})
